import React, { JSX } from "react";
import { Instant } from "@js-joda/core";
import { safeIdString } from "utils/utils";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { useCompanyTimezone } from "hooks/useTimezone";
import { Tooltip } from "primereact/tooltip";
import { checkIfDataInCurrentMonthExists } from "utils/time-utils";
import DiffValueDisplayer from "./DiffValueDisplayer";
import { formatDiff } from "./ValueChangeIndicatorUtils";
import { DataDisplayType, DiffValueOptions, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import DiffTooltip from "./DiffTooltip";
import NoDataInCurrentMonth from "./NoDataInCurrentMonth";

const DiffValueForLastValue = (
  props: ValueChangeIndicatorProps & {
    refTime: Instant;
    data?: DatasetDataInfoResponse[];
    currentData?: InstantDoubleValue[];
    currentDataset?: DatasetDataInfoResponse;
    currentDiffValueOptions: DiffValueOptions;
    diffDecimalPlaces?: number;
  }
) => {
  const companyTz = useCompanyTimezone();
  if (
    !props.displayLastValueDiff ||
    !props.currentData ||
    props.currentData.length < 2 ||
    props.dataDisplayType === DataDisplayType.AVERAGE_VALUE ||
    props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON ||
    props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON
  ) {
    return null;
  }

  const prev = props.currentData[0].value ?? 0;
  const curr = props.currentData[1].value ?? 0;

  const currentKey = props.currentData[1]?.key ?? props.currentData[0]?.key;
  const isInCurrentMonth =
    currentKey && checkIfDataInCurrentMonthExists(currentKey, companyTz, props.refTime);

  if (!isInCurrentMonth && props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART) {
    return <NoDataInCurrentMonth tz={companyTz} refTime={props.refTime} theme={props.theme} />;
  }

  const diffValue = props.currentDiffValueOptions.value ?? curr - prev;
  const safeDiffId = safeIdString(props.data?.[0]?.identifier ?? "default");
  const diffTooltipId = `last-value-diff-${safeDiffId}-tooltip`;

  const getDiffTooltip = (prevInfo?: JSX.Element, currInfo?: JSX.Element) => (
    <DiffTooltip
      prevInfo={prevInfo}
      currInfo={currInfo}
      currentData={props.currentData}
      currentDataset={props.currentDataset}
      currentDiffValueOptions={props.currentDiffValueOptions}
      dataDisplayType={props.dataDisplayType}
      diffDecimalPlaces={props.diffDecimalPlaces}
    />
  );

  return (
    <>
      <DiffValueDisplayer
        dataDisplayType={props.dataDisplayType}
        useDiffIndicatorColors={props.useDiffIndicatorColors}
        diffValue={diffValue}
        diffValueDecimalPlaces={props.diffValueDecimalPlaces}
        formattedValue={formatDiff(
          prev,
          curr,
          props.currentDataset?.unit,
          props.currentDiffValueOptions.value,
          false,
          props.diffType,
          props.diffDecimalPlaces,
          props.dataDisplayType
        )}
      />
      <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
        {getDiffTooltip()}
      </Tooltip>
    </>
  );
};

export default DiffValueForLastValue;
